import * as React from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import {
  format,
  addDays,
  subDays,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  addWeeks,
  subWeeks,
  isSameDay,
  isSameWeek,
  isSameMonth,
} from 'date-fns';
import './navigation.css';

interface Props {
  storageKey: string;
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  currentView: string;
  redesignActive?: boolean;
}

const JUMP_WEEKS_OPTIONS = [2, 4, 8];

function DatePickerHeader({
  currentMonth,
  onMonthChange,
}: {
  currentMonth: Date;
  onMonthChange: (date: Date, slideDirection: 'left' | 'right') => void;
}) {
  const navButtonSx = {
    width: 28,
    height: 28,
    borderRadius: '5px',
    border: '1px solid rgb(237,238,239)',
    overflow: 'hidden',
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        pt: 1.5,
      }}>
      <IconButton
        size="small"
        sx={navButtonSx}
        onClick={() => onMonthChange(subMonths(currentMonth, 1), 'right')}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography variant="h4">{format(currentMonth, 'MMMM yyyy')}</Typography>
      <IconButton
        size="small"
        sx={navButtonSx}
        onClick={() => onMonthChange(addMonths(currentMonth, 1), 'left')}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function DateNav({
  storageKey,
  selectedDate,
  setSelectedDate,
  currentView,
  redesignActive,
}: Props) {
  const [displayStartDate, setDisplayStartDate] = React.useState(selectedDate);
  const [displayEndDate, setDisplayEndDate] = React.useState(selectedDate);

  React.useEffect(() => {
    if (currentView === 'week') {
      setDisplayStartDate(startOfWeek(selectedDate));
      setDisplayEndDate(endOfWeek(selectedDate));
    }

    if (currentView === 'list') {
      setDisplayEndDate(addDays(selectedDate, 7));
    }
  }, [currentView, selectedDate]);

  const today = new Date();
  const isViewingToday =
    currentView === 'week'
      ? isSameWeek(selectedDate, today)
      : currentView === 'month'
      ? isSameMonth(selectedDate, today)
      : isSameDay(selectedDate, today);

  const commitDate = (newDate: Date) => {
    setSelectedDate(newDate);
    if (storageKey) sessionStorage.setItem(storageKey, newDate.toISOString());
  };

  const nextDates = () => {
    const adjustDate = (date: Date) => {
      if (currentView === 'month') return addMonths(startOfMonth(date), 1);
      if (currentView === 'week') return addWeeks(startOfWeek(date), 1);
      if (currentView === 'day') return addDays(date, 1);
      return addDays(date, 7);
    };

    commitDate(adjustDate(selectedDate));
  };

  const prevDates = () => {
    const adjustDate = (date: Date) => {
      if (currentView === 'month') return subMonths(startOfMonth(date), 1);
      if (currentView === 'week') return subWeeks(startOfWeek(date), 1);
      if (currentView === 'day') return subDays(date, 1);
      return subDays(date, 7);
    };

    commitDate(adjustDate(selectedDate));
  };

  const isDesktop = useMediaQuery('(min-width:900px)');
  const [pickerAnchorEl, setPickerAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const pickerOpen = Boolean(pickerAnchorEl);

  const openPicker = (event: React.MouseEvent<HTMLElement>) => {
    if (!redesignActive) return;
    setPickerAnchorEl(event.currentTarget);
  };
  const closePicker = () => setPickerAnchorEl(null);

  const handlePickDate = (newDate: Date | null) => {
    if (!newDate) return;
    commitDate(newDate);
    closePicker();
  };

  const handleJumpWeeks = (weeks: number) => {
    commitDate(addWeeks(selectedDate, weeks));
    closePicker();
  };

  const pickerContent = (
    <Box
      sx={{
        p: 2,
        width: '100%',
        maxWidth: 352,
        mx: 'auto',
        boxSizing: 'border-box',
      }}>
      <DateCalendar
        value={selectedDate}
        onChange={handlePickDate}
        slots={{ calendarHeader: DatePickerHeader }}
      />
      <Divider sx={{ my: 1 }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          px: 1,
          pb: 0.5,
        }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Jump ahead
        </Typography>
        {JUMP_WEEKS_OPTIONS.map(weeks => (
          <Chip
            key={weeks}
            label={`+${weeks}w`}
            onClick={() => handleJumpWeeks(weeks)}
            variant="outlined"
            clickable
            sx={{
              borderColor: '#000',
              color: 'text.primary',
              height: 30,
              typography: 'caption',
              fontWeight: 600,
            }}
          />
        ))}
      </Box>
    </Box>
  );

  const dateLabel = (
    <>
      {currentView === 'list' && (
        <Typography variant="h5" color="text.primary">
          {format(selectedDate, 'MMM d')} - {format(displayEndDate, 'MMM d')}
        </Typography>
      )}
      {currentView === 'week' && (
        <Typography variant="h5" color="text.primary">
          {format(displayStartDate, 'MMM d')} -{' '}
          {format(displayEndDate, 'MMM d')}
        </Typography>
      )}
      {currentView === 'month' && (
        <Typography variant="h5" color="text.primary">
          {format(selectedDate, 'MMMM')}
        </Typography>
      )}
      {currentView === 'day' && (
        <Typography variant="h5" color="text.primary">
          {format(selectedDate, 'eeee, MMM d')}
        </Typography>
      )}
      {redesignActive && <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
    </>
  );

  return (
    <div className="date-nav">
      {redesignActive && (
        <button
          type="button"
          className="date-nav-today-button"
          disabled={isViewingToday}
          onClick={() => commitDate(new Date())}>
          Today
        </button>
      )}
      <button className="date-nav-arrow" onClick={prevDates} type="button">
        <img className="chevron-left" src="/chevron-down.svg" alt="back" />
      </button>
      {redesignActive ? (
        <button
          type="button"
          className="date-nav-content date-nav-content-button"
          onClick={openPicker}>
          {dateLabel}
        </button>
      ) : (
        <div className="date-nav-content">{dateLabel}</div>
      )}
      <button className="date-nav-arrow" onClick={nextDates} type="button">
        <img className="chevron-right" src="/chevron-down.svg" alt="next" />
      </button>
      {redesignActive &&
        (isDesktop ? (
          <Popover
            open={pickerOpen}
            anchorEl={pickerAnchorEl}
            onClose={closePicker}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: { sx: { mt: 1, borderRadius: '10px' } },
            }}>
            {pickerContent}
          </Popover>
        ) : (
          <Drawer
            anchor="bottom"
            open={pickerOpen}
            onClose={closePicker}
            PaperProps={{
              sx: {
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
              },
            }}>
            {pickerContent}
          </Drawer>
        ))}
    </div>
  );
}

export default DateNav;
